import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Box, FormControlLabel, Checkbox } from '@mui/material';
import clsx from 'clsx';
import { fontWeight, Heading, headingLevel, TextError } from '../../../../../packages/ui/atoms/typography';
import { Select, selectType } from '../../../../../packages/ui/atoms/select';
import { Textarea } from '../../../../../packages/ui/atoms/textarea';
import { Input, inputSize, inputType } from '../../../../../packages/ui/atoms/input';
import { Button, btnType, btnSize } from '../../../../../packages/ui/atoms/button';
import { useDispatch, useSelector } from 'react-redux';
import { profileActionCreators } from '../../../../../redux/modules/profile';
import { getProfile } from '../../../../../redux/modules/profile/selectors';
import { getAuth } from '../../../../../redux/modules/auth/selectors';
// styles
import { useStyles } from './MedicationForm.styles';
import { MEDICATION_DOSES, MEDICATION_FREQUENCIES } from '../../../../../constants/CommonConstants';
import { generateSig, searchExternalMedications } from '../../../../../services/schedule/schedule.service';
import { showSnackbar } from '../../../../../redux/modules/snackbar';
import debounce from 'debounce';

const frequencies = [{ display: 'Select Frequency', abbr: '' }, ...MEDICATION_FREQUENCIES].map(frequency => ({
  label: frequency.display,
  title: frequency.display,
  value: frequency.abbr,
}));

const doses = MEDICATION_DOSES.map(dose => ({ label: dose, value: dose }));
const MedicationForm = ({ form, error, medication, onChangeHandler, state, patientDiagnoses }) => {
  const [externalMedications, setExternalMedications] = useState([]);
  const [generateSigVisible, setGenerateSigVisible] = useState(true);
  const [sigLoading, setSigLoading] = useState(false);
  const isMountedRef = useRef(true);
  const diagnosesOptions = patientDiagnoses?.map(diagnosis => diagnosis.taxonomyName) || [];
  const { providers } = useSelector(getProfile);
  const { meta } = useSelector(getAuth);
  const providerListOptions = providers
    .filter(provider => provider.active)
    .filter(provider => provider.designation === 'Nurse Practitioner' || provider.designation === 'Physician')
    .map(provider => ({
      label: provider.fullName,
      value: provider.providerId,
    }));

  const dispatch = useDispatch();

  const searchForExternalMedications = useCallback(async (searchQuery, selectedMedication) => {
    try {
      const response = await searchExternalMedications(searchQuery, selectedMedication);
      if (isMountedRef.current && response.data) {
        setExternalMedications(response.data);
      }
    } catch (error) {
      console.error('Error searching external medications:', error);
    }
  }, []);

  const drugSearchOptions = externalMedications.map(med => ({
    label: med.drug,
    value: med.id,
    drugDefaults: med.drugDefaults,
  }));

  const searchDelayed = useMemo(
    () => debounce(searchForExternalMedications, 1000),
    [searchForExternalMedications],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      searchDelayed.clear();
    };
  }, [searchDelayed]);

  /**
   * Handle external provider checkbox change with proper state management
   */
  const handleExternalProviderChange = () => {
    if (meta?.authority !== 'PRACTITIONER') return;

    const newIsExternalProvider = !state.isExternalProvider;
    onChangeHandler('isExternalProvider', newIsExternalProvider);

    // If unchecking (going back to own provider), reset to current user's ID
    if (!newIsExternalProvider) {
      const currentProvider = providers.find(provider => provider.providerId === meta?.userId);
      if (currentProvider) {
        onChangeHandler('confidantProvider', currentProvider.providerId);
        onChangeHandler('provider', 'Confidant Provider');
      }
    }
  };

  /**
   * Auto-populate form fields with drug defaults when a medication is selected
   */
  const populateDefaultValues = selectedMedication => {
    if (!selectedMedication?.drugDefaults) return;

    const { drugDefaults } = selectedMedication;

    // Map the default values to form fields
    if (drugDefaults.quantity !== undefined && drugDefaults.quantity !== null) {
      onChangeHandler('quantity', drugDefaults.quantity);
    }

    if (drugDefaults.dose) {
      // Find matching dose from predefined options
      const matchingDose = doses.find(dose => dose.value.toLowerCase() === drugDefaults.dose.toLowerCase());
      if (matchingDose) {
        onChangeHandler('dose', matchingDose.value);
      }
    }

    if (drugDefaults.frequency) {
      // Find the matching frequency object from the frequencies array
      const matchingFrequency = frequencies.find(freq => freq.value === drugDefaults.frequency);
      if (matchingFrequency) {
        onChangeHandler('frequency', matchingFrequency);
      }
    }

    if (drugDefaults.duration !== undefined && drugDefaults.duration !== null) {
      onChangeHandler('duration', drugDefaults.duration);
    }

    if (drugDefaults.refills !== undefined && drugDefaults.refills !== null) {
      // Ensure refills is a number and within valid range (0-12)
      const refillsValue = Number(drugDefaults.refills);
      if (refillsValue >= 0 && refillsValue <= 12 && Number.isInteger(refillsValue)) {
        onChangeHandler('refills', refillsValue);
      }
    }

    if (drugDefaults.sig) {
      onChangeHandler('sig', drugDefaults.sig);
    }

    if (drugDefaults.pharmacyNotes) {
      onChangeHandler('pharmacyNotes', drugDefaults.pharmacyNotes);
    }

    if (drugDefaults.note) {
      onChangeHandler('additionalNotes', drugDefaults.note);
    }

    // Force dispenseAmount calculation after all fields are populated
    setTimeout(() => {
      if (drugDefaults.quantity && drugDefaults.frequency && drugDefaults.duration) {
        const frequencyQuantity = MEDICATION_FREQUENCIES.find(
          f => f.abbr === drugDefaults.frequency,
        )?.quantity;
        if (frequencyQuantity !== null && frequencyQuantity !== undefined) {
          const calculatedAmount = Math.ceil(
            drugDefaults.quantity * frequencyQuantity * drugDefaults.duration,
          );
          onChangeHandler('dispenseAmount', calculatedAmount);
        }
      }
    }, 100);
  };

  useEffect(() => {
    dispatch(
      profileActionCreators.fetchProviders({
        searchQuery: '',
        pageNumber: 1,
        pageSize: 1000,
        orderBy: 'asc',
        sortBy: '',
      }),
    );
  }, []);

  useEffect(() => {
    if (medication?.item?.value && externalMedications.length === 0) {
      searchForExternalMedications('', medication.item.value);
    }
  }, [medication?.item?.value]);

  const classes = useStyles();
  const { values, touched, errors, setFieldValue, ...rest } = form;

  const setTouched = name => async () => {
    await rest.setTouched({ ...touched, [name]: true });
  };

  const onFrequencyChange = value => {
    onChangeHandler('frequency', value);
    if (value?.value === 'CUSTOM') {
      onChangeHandler('sig', '');
      onChangeHandler('dispenseAmount', '');
      setGenerateSigVisible(false);
    } else {
      setGenerateSigVisible(true);
      const frequencyQuantity = MEDICATION_FREQUENCIES.find(f => f.abbr === value?.value)?.quantity;
      if (!frequencyQuantity) {
        onChangeHandler('dispenseAmount', '');
      }
    }
  };

  const renderLabel = (str, className = '') => (
    <Heading level={headingLevel.S} className={clsx(classes.label, className)} weight={fontWeight.BOLD}>
      {str}
    </Heading>
  );

  useEffect(() => {
    if (values?.status === '') {
      onChangeHandler('status', 'INACTIVE');
    }
  }, [values]);

  useEffect(() => {
    const selectedFrequency = frequencies.find(f => f.value === state.frequency?.value);
    if (selectedFrequency?.value && state.quantity && state.duration) {
      if (selectedFrequency.value === 'CUSTOM') {
        setGenerateSigVisible(false);
      }
      const frequencyQuantity = MEDICATION_FREQUENCIES.find(
        f => f.abbr === selectedFrequency.value,
      )?.quantity;
      if (frequencyQuantity !== null && frequencyQuantity !== undefined) {
        const calculatedAmount = Math.ceil(state.quantity * frequencyQuantity * state.duration);
        onChangeHandler('dispenseAmount', calculatedAmount);
      }
    }
  }, [state.frequency, state.quantity, state.duration]);

  const isSigDisabled =
    !state.dose ||
    state.dose === '' ||
    !state.frequency ||
    state.frequency === '' ||
    state.frequency?.value === '' ||
    !state.quantity ||
    state.quantity === '' ||
    !state.duration ||
    state.duration === '';

  const handleSigGeneration = async () => {
    setSigLoading(true);
    try {
      const request = {
        quantity: state.quantity,
        dose: state.dose,
        frequency: state.frequency.value ?? state.frequency,
        duration: state.duration,
      };
      const { data } = await generateSig(request);
      if (data?.data?.sig) {
        onChangeHandler('sig', data.data.sig);
      } else {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: 'Failed to generate sig',
          }),
        );
      }
    } catch (error) {
      console.warn(error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to generate sig',
        }),
      );
    }
    setSigLoading(false);
  };
  return (
    <Box className={classes.formContent}>
      {error && (
        <Box display="flex" justifyContent="center">
          <TextError errorMsg={error} />
        </Box>
      )}
      <Box className={classes.section}>
        {renderLabel('Drug')}
        <Input
          fullWidth
          variant={inputType.AUTOCOMPLETE}
          filterSelectedOptions={false}
          name="externalRx"
          options={drugSearchOptions}
          getOptionLabel={option => option?.label ?? option?.drug ?? option}
          getOptionDisabled={option => !option || option?.value === '' || option === ''}
          isOptionEqualToValue={(option, value) => {
            return option?.value === value?.value || option?.value === value;
          }}
          value={drugSearchOptions.find(option => option.value === state.externalRx?.value) || null}
          size={inputSize.M}
          placeholder="Search for a drug"
          onChange={(event, value) => {
            onChangeHandler('externalRx', value);
            // Auto-populate form fields with drug defaults if available
            if (value?.drugDefaults) {
              populateDefaultValues(value);
            }
          }}
          onInputChange={(event, ev2) => {
            if (!event) return;
            if (
              event.code === 'Enter' ||
              event.code === 'NumpadEnter' ||
              event.type === 'click' ||
              event.type === 'blur'
            ) {
              return;
            }
            if (event.target && event.target.value) {
              searchDelayed(event.target.value, '');
            }
          }}
          onBlur={setTouched('externalRx')}
        />
        <TextError errorMsg={touched.externalRx ? errors.externalRx?.toString() : null} />
      </Box>
      <Box display="flex" flexDirection="row" gap="24px">
        <Box className={classes.section}>
          {renderLabel('Quantity')}
          <Input
            fullWidth
            name="quantity"
            value={state.quantity}
            size={inputSize.M}
            placeholder="Enter Quantity"
            onChange={event => {
              if (event.target.value === '') {
                return onChangeHandler('quantity', '');
              } else {
                if (Number(event.target.value) > 0) {
                  return onChangeHandler('quantity', Number(event.target.value));
                }
              }
            }}
            type="number"
            onBlur={setTouched('quantity')}
          />
          <TextError errorMsg={touched.quantity ? errors.quantity?.toString() : null} />
        </Box>
        <Box className={classes.section}>
          {renderLabel('Dose')}
          <Select
            name="dose"
            value={state.dose}
            variant={selectType.SECONDARY}
            options={doses?.sort((a, b) => a.label.localeCompare(b.label))}
            emptyText="Select item"
            displayEmpty
            onChange={event => onChangeHandler('dose', event.target.value)}
          />
          <TextError errorMsg={touched.dose ? errors.dose?.toString() : null} />
        </Box>
      </Box>
      <Box display="flex" flexDirection="row" gap="24px">
        <Box className={classes.section}>
          {renderLabel('Frequency')}
          <Input
            fullWidth
            variant={inputType.AUTOCOMPLETE}
            filterSelectedOptions={false}
            name="frequency"
            options={frequencies || []}
            getOptionLabel={option => option?.label ?? option}
            getOptionDisabled={option => !option || option?.value === '' || option === ''}
            isOptionEqualToValue={(option, value) => {
              return option.value === value || option.value === value?.value;
            }}
            value={state.frequency}
            size={inputSize.M}
            placeholder="Enter or Select Frequency"
            onChange={(event, value) => {
              onFrequencyChange(value);
            }}
            onBlur={setTouched('frequency')}
          />
          <TextError errorMsg={touched.frequency ? errors.frequency?.toString() : null} />
        </Box>
        <Box className={classes.section}>
          {renderLabel('Duration')}
          <Input
            fullWidth
            name="duration"
            value={state.duration}
            size={inputSize.M}
            endAdornment="Days"
            placeholder="Enter Duration"
            onChange={event => {
              if (event.target.value === '') {
                return onChangeHandler('duration', '');
              } else {
                if (Number(event.target.value) > 0) {
                  return onChangeHandler('duration', Math.trunc(Number(event.target.value)));
                }
              }
            }}
            type="number"
            onBlur={setTouched('duration')}
          />
          <TextError errorMsg={touched.duration ? errors.duration?.toString() : null} />
        </Box>
      </Box>
      <Box className={classes.section}>
        {renderLabel('Sig')}
        <Input
          fullWidth
          name="sig"
          value={state.sig}
          size={inputSize.M}
          placeholder="Enter Sig (Directions)"
          endAdornment={
            generateSigVisible && (
              <Button
                disabled={isSigDisabled || sigLoading}
                size={btnSize.XSMALL}
                loading={sigLoading}
                variant={btnType.SECONDARY}
                onClick={handleSigGeneration}
              >
                Generate
              </Button>
            )
          }
          onChange={event => onChangeHandler('sig', event.target.value)}
          onBlur={setTouched('sig')}
        />
        <TextError errorMsg={touched.sig ? errors.sig?.toString() : null} />
      </Box>
      <Box display="flex" flexDirection="row" gap="24px">
        <Box className={classes.section}>
          {renderLabel('Dispense Amount')}
          <Input
            fullWidth
            name="dispenseAmount"
            value={state.dispenseAmount}
            size={inputSize.M}
            placeholder="Enter Dispense Amount"
            endAdornment={state.dose && state.dose !== '' ? state.dose : ''}
            onChange={event => {
              if (event.target.value === '') {
                return onChangeHandler('dispenseAmount', '');
              } else {
                if (Number(event.target.value) > 0) {
                  return onChangeHandler('dispenseAmount', Math.trunc(Number(event.target.value)));
                }
              }
            }}
            type="number"
            onBlur={setTouched('dispenseAmount')}
          />
          <TextError errorMsg={touched.dispenseAmount ? errors.dispenseAmount?.toString() : null} />
        </Box>
        <Box className={classes.section}>
          {renderLabel('Refills')}
          <Select
            name="refills"
            value={state.refills}
            variant={selectType.SECONDARY}
            options={Array.of(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12).map(item => ({
              label: item,
              value: item,
            }))}
            onChange={event => {
              onChangeHandler('refills', event.target.value);
            }}
          />
        </Box>
      </Box>
      <Box className={classes.section}>
        {renderLabel('Associated Diagnoses')}
        <Input
          fullWidth
          name="associatedDiagnoses"
          value={state.associatedDiagnoses}
          options={diagnosesOptions}
          variant={inputType.TAGS}
          size={inputSize.M}
          getOptionLabel={option => option}
          onBlur={setTouched('associatedDiagnoses')}
          onChange={async (_, newValue) => {
            await onChangeHandler('associatedDiagnoses', newValue);
          }}
        />
      </Box>
      <Box className={classes.section}>
        {renderLabel('Note to Pharmacy')}
        <Textarea
          name="pharmacyNotes"
          value={state.pharmacyNotes}
          placeholder="Enter note to pharmacy"
          onChange={event => onChangeHandler('pharmacyNotes', event.target.value)}
          minRows={2}
          maxRows={3}
          onBlur={setTouched('pharmacyNotes')}
        />
        <TextError errorMsg={touched.pharmacyNotes ? errors.pharmacyNotes?.toString() : null} />
      </Box>
      <Box className={classes.section}>
        {renderLabel('Note to Confidant Team')}
        <Textarea
          name="additionalNotes"
          value={state.additionalNotes}
          placeholder="Enter your notes"
          onChange={event => onChangeHandler('additionalNotes', event.target.value)}
          minRows={2}
          maxRows={3}
          onBlur={setTouched('additionalNotes')}
        />
        <TextError errorMsg={touched.additionalNotes ? errors.additionalNotes?.toString() : null} />
      </Box>

      <Box className={classes.section}>
        <FormControlLabel
          key={'allowSubstitutions'}
          control={<Checkbox checked={state.allowSubstitutions} />}
          onChange={() => onChangeHandler('allowSubstitutions', !state.allowSubstitutions)}
          label="Substitutions Allowed"
        />
      </Box>

      <Box className={classes.section}>
        <FormControlLabel
          key={'isExternalProvider'}
          control={
            <Checkbox checked={state.isExternalProvider} disabled={meta?.authority !== 'PRACTITIONER'} />
          }
          onChange={handleExternalProviderChange}
          label="I'm recording another provider's prescription"
        />
      </Box>
      {/* Show provider fields only if: Admin (always) OR Provider with checkbox checked */}
      {(meta?.authority !== 'PRACTITIONER' || state.isExternalProvider) && (
        <>
          <Box className={classes.section}>
            {renderLabel('Prescribing Provider')}
            <Select
              name="provider"
              value={state.provider || 'Confidant Provider'}
              variant={selectType.SECONDARY}
              options={[{ label: 'Confidant Provider', value: 'Confidant Provider' }]}
              onChange={event => {
                onChangeHandler('provider', event.target.value);
              }}
            />
            <TextError errorMsg={touched.provider ? errors.provider?.toString() : null} />
          </Box>
          <Box className={classes.section}>
            {renderLabel('Confidant Provider')}
            <Select
              name="confidantProvider"
              value={state.confidantProvider}
              variant={selectType.SECONDARY}
              options={providerListOptions?.sort((a, b) => a.label.localeCompare(b.label)) || []}
              emptyText="Select item"
              displayEmpty
              onChange={event => onChangeHandler('confidantProvider', event.target.value)}
            />
            <TextError errorMsg={touched.confidantProvider ? errors.confidantProvider?.toString() : null} />
          </Box>
        </>
      )}
    </Box>
  );
};

export { MedicationForm };
